import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resolveIconComponent } from '@/lib/menuIcons';
import { cn } from '@/lib/utils';
import { useMenu } from '@/context/MenuContext';
import { MOBILE_TABS } from '@/constants/menuCodes';
import MobileMenuDrawer from './MobileMenuDrawer';

/**
 * 모바일 하단 탭바.
 * PC 화면을 그대로 줄이면 결재/업무 같은 상시 진입점에 도달하기 어렵다.
 *
 * menuCode 가 붙은 탭은 해당 메뉴에 접근 권한이 있을 때만 노출되므로,
 * 전자결재 모듈을 붙이면 이 컴포넌트를 수정하지 않아도 '결재' 탭이 자동으로 나타난다.
 */
const BottomTabBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { flatMenus, badges } = useMenu();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const tabs = useMemo(() => {
    const byCode = new Map();
    flatMenus.forEach((menu) => { if (menu.menuCode) byCode.set(menu.menuCode, menu); });

    return MOBILE_TABS.map((tab) => {
      if (!tab.menuCode) return tab;
      const menu = byCode.get(tab.menuCode);
      // 권한이 없거나 아직 존재하지 않는 메뉴는 탭에서 제외
      if (!menu?.urlPath) return null;
      return { ...tab, to: menu.urlPath };
    }).filter(Boolean);
  }, [flatMenus]);

  const handleTabClick = (tab) => {
    if (tab.action === 'drawer') {
      setIsDrawerOpen((prev) => !prev);
      return;
    }
    setIsDrawerOpen(false);
    if (tab.to) navigate(tab.to);
  };

  const isTabActive = (tab) => {
    if (tab.action === 'drawer') return isDrawerOpen;
    if (!tab.to) return false;
    return location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`);
  };

  return (
    <>
      <MobileMenuDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />

      <nav
        className="fixed inset-x-0 bottom-0 z-[96] flex border-t border-chrome-border bg-chrome pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="주요 메뉴"
      >
        {tabs.map((tab) => {
          const Icon = resolveIconComponent(tab.icon);
          const active = isTabActive(tab);
          const badgeCount = tab.badgeKey ? badges[tab.badgeKey] : 0;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={cn(
                'relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
                active ? 'font-bold text-chrome-accent' : 'text-chrome-muted'
              )}
            >
              {/* 선택된 탭 위쪽 강조선 — 색만으로는 작은 화면에서 잘 안 보인다 */}
              {active && (
                <span className="absolute inset-x-3 top-0 h-[3px] rounded-b-full bg-chrome-accent" />
              )}
              <span className="relative">
                <Icon size={20} />
                {badgeCount > 0 && (
                  <span className="absolute -right-2 -top-1 min-w-[16px] rounded-full bg-red-500 px-1 text-[9px] font-bold leading-4 text-white">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </span>
              {tab.label}
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default BottomTabBar;
